import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ClipboardCheck, User, Car, Gauge, Fuel,
  AlertTriangle, ShieldAlert, PenLine, X, ChevronLeft, ChevronRight, FileText,
} from "lucide-react";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";
import CarDiagram from "./components/CarDiagram";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  draft: "bg-white/15 text-white/70",
  in_progress: "bg-blue-500/25 text-blue-300",
  completed: "bg-emerald-500/25 text-emerald-300",
  signed: "bg-purple-500/25 text-purple-300",
};
const SEV_STYLE = {
  minor: "bg-sky-500/15 border-sky-500/40 text-sky-300",
  moderate: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  major: "bg-red-500/15 border-red-500/40 text-red-300",
};
const KIND_LABELS = {
  front: "Front", front_left: "Front Left", driver_side: "Driver Side",
  rear_left: "Rear Left", rear: "Rear", rear_right: "Rear Right",
  passenger_side: "Passenger Side", front_right: "Front Right",
  windshield: "Windshield", interior: "Interior", wheels: "Wheels", extra: "Additional",
};
const VIEW_ORDER = ["top", "front", "driver", "rear", "passenger", "interior"];
const FUEL = { empty: "Empty", quarter: "1/4", half: "1/2", three_quarter: "3/4", full: "Full" };

export default function InspectionDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);       // { inspection, photos, markers, customer, vehicle }
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index into photoList

  useEffect(() => {
    Promise.all([
      CRM_API.get(`/inspections/${id}`),
      CRM_API.get("/services"),
    ]).then(([insp, svc]) => {
      setData(insp.data);
      const list = Array.isArray(svc.data) ? svc.data : [];
      setServices(Object.fromEntries(list.map((s) => [s.id, s.name])));
    }).catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const photoUrl = (p) => `${BACKEND}/api/crm/inspections/${id}/photos/${p.id}/file`;
  const photoList = useMemo(() => (data?.photos || []), [data]);

  if (loading) return <div className="p-10 text-white/40 text-sm">Loading report…</div>;
  if (!data) return <div className="p-10 text-white/40 text-sm">Report not found.</div>;

  const { inspection: ins, photos, markers, customer: c, vehicle: v } = data;
  const info = ins.vehicle_info || {};
  const sig = ins.signature;
  const lights = (info.warning_lights || "").split(",").map((s) => s.trim()).filter(Boolean);
  const svcNames = (info.services_requested || []).map((sid) => services[sid]).filter(Boolean);

  return (
    <div data-testid="crm-inspection-detail">
      <PageHeader
        eyebrow="Studio"
        title="Check-In Report"
        subtitle={`${new Date(ins.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })} · ${new Date(ins.created_at).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`}
        right={(
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded text-[10px] font-mono-alt uppercase tracking-widest ${STATUS[ins.status] || STATUS.draft}`} data-testid="insp-status">
              {ins.status}
            </span>
            {ins.work_order_id && (
              <Link to={`/crm/work-orders/${ins.work_order_id}`} data-testid="insp-wo-link"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white/5 border border-white/12 text-white/70 hover:text-white text-sm transition-colors">
                <FileText className="w-4 h-4" /> Work Order
              </Link>
            )}
          </div>
        )}
      />
      <div className="px-6 md:px-10 py-6 max-w-5xl mx-auto space-y-8">
        <Link to="/crm/inspections" className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-xs font-mono-alt uppercase tracking-widest transition-colors" data-testid="insp-back">
          <ArrowLeft className="w-3.5 h-3.5" /> All reports
        </Link>

        {/* Vehicle + Customer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="insp-vehicle-card">
            <div className="flex items-center gap-2 mb-2">
              <Car className="w-4 h-4 text-[#1F5EFF]" />
              <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Vehicle</div>
            </div>
            <div className="text-white font-display text-xl">
              {[v?.year, v?.make, v?.model].filter(Boolean).join(" ") || "Vehicle"}
            </div>
            <div className="mt-1.5 text-sm text-white/50 space-x-3">
              {v?.trim && <span>{v.trim}</span>}
              {v?.color && <span>{v.color}</span>}
              {v?.license_plate && <span className="font-mono-alt text-white/70">{v.license_plate}</span>}
            </div>
            {v?.vin && <div className="mt-1 text-[11px] font-mono-alt text-white/35">VIN {v.vin}</div>}
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="insp-customer-card">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-[#1F5EFF]" />
              <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Customer</div>
            </div>
            <Link to={`/crm/customers/${c?.id}`} className="text-white font-display text-xl hover:text-[#8db2ff] transition-colors">
              {`${c?.first_name || ""} ${c?.last_name || ""}`.trim() || "Customer"}
            </Link>
            <div className="mt-1.5 text-sm text-white/50 space-x-3">
              {c?.phone && <span>{c.phone}</span>}
              {c?.email && <span>{c.email}</span>}
            </div>
          </div>
        </div>

        {/* Condition info */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="insp-info-card">
          <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest mb-4">Vehicle condition</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono-alt text-white/40 uppercase tracking-widest"><Gauge className="w-3 h-3" /> Mileage</div>
              <div className="mt-1 text-white" data-testid="insp-mileage">{info.mileage ? `${Number(info.mileage).toLocaleString()} km` : "—"}</div>
            </div>
            <div>
              <div className="flex items-center gap-1.5 text-[10px] font-mono-alt text-white/40 uppercase tracking-widest"><Fuel className="w-3 h-3" /> Fuel</div>
              <div className="mt-1 text-white" data-testid="insp-fuel">{FUEL[info.fuel_level] || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Keys received</div>
              <div className="mt-1 text-white" data-testid="insp-keys">{info.keys_received ?? "—"}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Existing protection</div>
              <div className="mt-1 text-white text-sm" data-testid="insp-protection">
                {[info.existing_ppf && "PPF", info.existing_tint && "Tint"].filter(Boolean).join(" + ") || "None"}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-1.5 text-[10px] font-mono-alt text-white/40 uppercase tracking-widest"><AlertTriangle className="w-3 h-3" /> Warning lights</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="insp-lights">
              {lights.length
                ? lights.map((l) => (
                  <span key={l} className="px-2 py-0.5 rounded-full bg-amber-500/12 border border-amber-500/35 text-amber-300 text-[11px]">{l}</span>
                ))
                : <span className="text-white/50 text-sm">None</span>}
            </div>
          </div>
          {svcNames.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/8">
              <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest mb-1.5">Services requested</div>
              <div className="flex flex-wrap gap-1.5" data-testid="insp-services">
                {svcNames.map((n) => (
                  <span key={n} className="px-2.5 py-1 rounded-full bg-[#1F5EFF]/12 border border-[#1F5EFF]/40 text-[#8db2ff] text-[11px]">{n}</span>
                ))}
              </div>
            </div>
          )}
          {(info.previous_repairs || info.valuables) && (
            <div className="mt-4 pt-4 border-t border-white/8 space-y-3">
              {info.previous_repairs && (
                <div>
                  <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Previous repairs / known damage</div>
                  <p className="mt-1 text-sm text-white/70 whitespace-pre-wrap">{info.previous_repairs}</p>
                </div>
              )}
              {info.valuables && (
                <div>
                  <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Valuables in vehicle</div>
                  <p className="mt-1 text-sm text-white/70 whitespace-pre-wrap">{info.valuables}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div data-testid="insp-photos">
            <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest mb-3">Photos · {photos.length}</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {photos.map((p, i) => (
                <button key={p.id} onClick={() => setLightbox(i)}
                  className="group relative aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-[#1F5EFF]/60 transition-colors"
                  data-testid={`insp-photo-${p.id}`}>
                  <img src={photoUrl(p)} alt={KIND_LABELS[p.kind] || p.kind} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[9px] font-mono-alt text-white/70 uppercase">
                    {KIND_LABELS[p.kind] || p.kind}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Damage markers */}
        <div data-testid="insp-damage">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-[#1F5EFF]" />
            <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">
              Damage markers · {markers.length}
            </div>
          </div>
          {markers.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-white/35 text-sm">No damage recorded</div>
          ) : (
            <div className="space-y-6">
              {VIEW_ORDER.filter((view) => markers.some((m) => m.view === view)).map((view) => {
                const vm = markers.filter((m) => m.view === view);
                return (
                  <div key={view} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <CarDiagram view={view} markers={markers} readonly />
                    <div className="space-y-2">
                      {vm.map((m) => {
                        const idx = markers.indexOf(m) + 1;
                        const mPhotos = (m.photo_ids || [])
                          .map((pid) => photoList.find((p) => p.id === pid))
                          .filter(Boolean);
                        return (
                          <div key={m.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`insp-marker-${m.id}`}>
                            <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-[10px] font-bold ${SEV_STYLE[m.severity] || SEV_STYLE.minor}`}>
                              {idx}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white capitalize">{m.type} · <span className="text-white/50">{m.severity}</span></div>
                              {m.description && <div className="text-xs text-white/45 mt-0.5">{m.description}</div>}
                              {mPhotos.length > 0 && (
                                <div className="flex gap-1.5 mt-2">
                                  {mPhotos.map((p) => (
                                    <button key={p.id} onClick={() => setLightbox(photoList.indexOf(p))}
                                      className="w-14 h-14 rounded-lg overflow-hidden border border-white/10 hover:border-[#1F5EFF]/60"
                                      data-testid={`insp-marker-photo-${m.id}`}>
                                      <img src={photoUrl(p)} alt="Damage" loading="lazy" className="w-full h-full object-cover" />
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Signature */}
        {sig && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="insp-signature">
            <div className="flex items-center gap-2 mb-3">
              <PenLine className="w-4 h-4 text-[#1F5EFF]" />
              <div className="text-[10px] font-mono-alt text-white/40 uppercase tracking-widest">Customer acceptance</div>
            </div>
            <div className="bg-white rounded-xl p-3 inline-block max-w-md w-full">
              <img src={sig.data_url} alt="Customer signature" className="w-full h-32 object-contain" data-testid="insp-signature-img" />
            </div>
            <div className="mt-3 text-sm text-white/70">
              Signed by <span className="text-white font-medium">{sig.customer_name}</span>
              {sig.signed_at && <> · {new Date(sig.signed_at).toLocaleString("en-CA")}</>}
            </div>
            {ins.acceptance_text && (
              <p className="mt-2 text-xs text-white/40 leading-relaxed max-w-2xl">{ins.acceptance_text}</p>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && photoList[lightbox] && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="insp-lightbox">
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="insp-lightbox-close">
            <X className="w-5 h-5" />
          </button>
          {lightbox > 0 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="insp-lightbox-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {lightbox < photoList.length - 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightbox(lightbox + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="insp-lightbox-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          <img src={photoUrl(photoList[lightbox])} alt="Inspection" className="max-w-full max-h-[85vh] object-contain rounded-xl" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/60 text-white/70 text-xs font-mono-alt uppercase tracking-widest">
            {KIND_LABELS[photoList[lightbox].kind] || photoList[lightbox].kind} · {lightbox + 1}/{photoList.length}
          </div>
        </div>
      )}
    </div>
  );
}
